import { Flame } from 'lucide-react';

import { useTransientFlag } from '@/hooks/ui/useTransientFlag';

interface ComboBadgeProps {
  /** Current consecutive-success streak length within the session. */
  comboLength: number;
  /** Bumped every time a new rating/answer fires so the toast can
   *  reset its visibility timer (otherwise React's same-value setState
   *  bail-out would suppress re-runs). */
  bumpKey: number;
  /** How long the badge stays visible after a fresh increment. */
  durationMs?: number;
  /** Minimum combo length before the badge appears. */
  threshold?: number;
}

/**
 * Tiny in-session momentum cue - shows "🔥 N" for ~1s when the user
 * extends a streak of ≥ `threshold` correct/Good+ answers in a row.
 * Silently disappears; no penalty, no toast on break.
 */
export function ComboBadge({
  comboLength,
  bumpKey,
  durationMs = 1100,
  threshold = 3,
}: ComboBadgeProps) {
  // Keyed on bumpKey, not comboLength: extending a streak to the same length
  // (or re-rating) must restart the window, and React's same-value setState
  // bail-out would otherwise swallow it.
  const visible = useTransientFlag(
    bumpKey,
    durationMs,
    comboLength >= threshold
  );

  if (!visible) return null;

  const display = comboLength >= 10 ? '10+' : String(comboLength);
  const tone =
    comboLength >= 10
      ? 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-900/30 dark:text-rose-300'
      : comboLength >= 5
        ? 'border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
        : 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300';

  return (
    <span
      role='status'
      aria-live='polite'
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold tabular-nums motion-safe:duration-150 motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-90 ${tone}`}
    >
      <Flame className='h-3 w-3' />
      <span>{display}</span>
      {/* Full alpha, subordinated by WEIGHT. `opacity-70` measured 2.89:1 on
          the amber tier and 2.99 on orange — and a badge is 11px text on a
          tint, which is the least forgiving combination on this route. */}
      <span className='font-normal'>in a row</span>
    </span>
  );
}

/**
 * Pure helper - given the previous combo length and the latest event's
 * "was this a success?" flag, returns the next combo length. Centralised
 * so the runner doesn't repeat the same `(success ? prev+1 : 0)` logic.
 */
export function nextCombo(prev: number, success: boolean): number {
  return success ? prev + 1 : 0;
}
